/**
 * A snap back, in the phone's own camera.
 *
 * The phone's camera, not one drawn in the page: a picture taken through the
 * browser's own camera feed loses the phone's processing -- Smart HDR and
 * Night mode on an iPhone, HDR+ on a Pixel -- and a snap looked at in a dim
 * kitchen shows it.
 *
 * Photo and video are asked for separately because that is the only way that
 * works on Android: Chrome opens the camera only for an input that accepts one
 * kind, and one that accepts both gets the photo picker instead. (An iPhone
 * would take both at once; Android is the one that decides this.) The same
 * inputs the camera page uses, for the same reason.
 *
 * Opened from inside the tap, because a browser only opens the camera from a
 * gesture. What it hands back is held up in the snap back sheet to be written
 * on, the way a snap to everyone is, before it goes.
 */
import { SNAP_MAX_VIDEO_SECONDS, snapClipTooLong } from '../../shared/constants';
import type { SnapDecor } from '../../shared/types';
import { inspectVideo } from './media/video';
import { enqueueUpload } from './uploadQueue';

export type SnapBackKind = 'photo' | 'video';

/** One input per kind, reused, so each snap back does not leave another. */
const inputs: Partial<Record<SnapBackKind, HTMLInputElement>> = {};

function inputFor(kind: SnapBackKind): HTMLInputElement {
  const made = inputs[kind];
  if (made) return made;
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = kind === 'photo' ? 'image/*' : 'video/*';
  // The camera page's own settings: the back lens for a photo, and the phone's
  // choice for a clip, where naming a lens has made some Androids refuse.
  input.setAttribute('capture', kind === 'photo' ? 'environment' : '');
  input.hidden = true;
  document.body.appendChild(input);
  inputs[kind] = input;
  return input;
}

/** Opens the camera, and hands whatever it comes back with to `onShot`. */
export function openSnapBack(kind: SnapBackKind, onShot: (file: File | undefined) => void): void {
  const input = inputFor(kind);
  input.value = '';
  input.onchange = () => onShot(input.files?.[0]);
  input.click();
}

/**
 * Whether what the camera handed back can go as a snap, and whether it is a
 * clip. Says why not through `tell`, and says nothing when there was no shot.
 */
export async function checkSnapShot(
  file: File | undefined,
  tell: (message: string) => void,
): Promise<{ video: boolean } | null> {
  if (!file) return null;
  const video = file.type.startsWith('video/');
  if (!video && !file.type.startsWith('image/')) {
    tell('That is not a photo or a clip.');
    return null;
  }

  // Turned away here rather than failing in the queue; createUpload refuses
  // a long clip too, and the transcoder cuts at the limit whatever it is told.
  if (video) {
    try {
      const facts = await inspectVideo(file);
      if (snapClipTooLong(facts.durationSec)) {
        tell(
          `That clip is ${Math.round(facts.durationSec)} seconds. A snap can be up to ${SNAP_MAX_VIDEO_SECONDS}.`,
        );
        return null;
      }
    } catch {
      tell('That video could not be read.');
      return null;
    }
  }
  return { video };
}

/**
 * Queues a snap: to everyone, or as a snap back when it names what it answers.
 * The decor goes only when there is some, so a plain snap is sent as before.
 */
export function queueSnap(
  file: File,
  video: boolean,
  { replyTo, decor }: { replyTo?: string; decor?: SnapDecor } = {},
): void {
  const target = {
    kind: 'snap' as const,
    id: crypto.randomUUID(),
    ...(replyTo ? { replyTo } : {}),
    ...(decor && Object.keys(decor).length ? { decor } : {}),
  };
  enqueueUpload(video ? { kind: 'chat-video', file, target } : { kind: 'chat-photo', file, target });
}
